import { useEffect, useMemo } from 'react';

import {
    IPortfolioSkillGroupsQueryVariables,
    ISkillGroupFragment,
    usePortfolioSkillGroupsQuery,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';

interface IProps {
    variables: IPortfolioSkillGroupsQueryVariables;
    skip?: boolean;
}

export const useSkillGroupsList = ({ variables, skip }: IProps) => {
    const { showApolloError } = useApolloError();

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    const queryVariables = useMemo(
        () => ({
            ...variables,
            ...paginationVariables,
        }),
        [variables, paginationVariables]
    );

    const { data, loading } = usePortfolioSkillGroupsQuery({
        variables: queryVariables,
        skip,
        onError: showApolloError,
    });

    const skillGroupsObject = data?.paginatedGroups;
    const skillGroupsCount = skillGroupsObject?.count;
    const skillGroups =
        (skillGroupsObject?.edges
            .map((edge) => edge?.node || null)
            .filter(Boolean) as ISkillGroupFragment[]) || [];

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(skillGroupsCount || 0);
    }, [skillGroupsCount, loading, initializePagination]);

    return {
        skillGroups,
        skillGroupsCount,
        loading,
        paginationSettings,
        setPage,
    };
};
